import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Users,
  Briefcase,
  TrendingUp,
  DollarSign,
  Calendar,
  Star,
  ArrowUpRight,
  Plus,
  Filter,
  Search,
  Clock,
  CheckCircle,
  Shield,
  FileText,
  AlertTriangle,
  Crown,
  Zap,
  Star as StarIcon,
  Building2,
  Bell
} from 'lucide-react'
import { authService } from '../../services/authService'
import { companyService } from '../../services/companyService'

const CompanyDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview')
  const [apiData, setApiData] = useState({})
  const navigate = useNavigate()
  const location = useLocation()
  const hasCheckedStatus = useRef(false)
  const isCheckingStatus = useRef(false)

  // Company status and subscription
  const companyStatus = {
    status: apiData.status,
    subscriptionTier: apiData.plan_name,
    complianceScore: apiData.compliance_score,
    expiringDocuments: apiData.expiring_documents,
    pendingActions: apiData.pending_actions
  }


  const subscriptionFeatures = {
    'Startup': {
      icon: <Zap className="w-5 h-5" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      features: ['5 Job Posts', 'Basic Search', 'Email Support']
    },
    'Corporate': {
      icon: <Building2 className="w-5 h-5" />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      features: ['25 Job Posts', 'Advanced Search', 'Priority Support', 'Analytics']
    },
    'Enterprise': {
      icon: <Crown className="w-5 h-5" />,
      color: 'text-warning-600',
      bgColor: 'bg-warning-50',
      features: ['Unlimited Posts', 'AI Matching', 'Dedicated Manager', 'Custom Integration']
    }
  }

  const complianceAlerts = [
    {
      type: 'warning',
      message: 'GST Certificate expires in 30 days',
      action: 'Renew Now'
    },
    {
      type: 'info',
      message: 'PAN verification pending',
      action: 'Verify'
    }
  ]

  const stats = [
    {
      title: 'Active Job Posts',
      value: '12',
      change: '+3',
      changeType: 'positive',
      icon: <Briefcase className="w-6 h-6" />
    },
    {
      title: 'Applications',
      value: '156',
      change: '+24',
      changeType: 'positive',
      icon: <Users className="w-6 h-6" />
    },
    {
      title: 'Interviews Scheduled',
      value: '8',
      change: '+2',
      changeType: 'positive',
      icon: <Calendar className="w-6 h-6" />
    },
    {
      title: 'Hired This Month',
      value: '5',
      change: '+2',
      changeType: 'positive',
      icon: <CheckCircle className="w-6 h-6" />
    }
  ]

  // Check user verification status on component mount
  useEffect(() => {
    // Prevent multiple simultaneous calls and navigation loops
    if (isCheckingStatus.current || hasCheckedStatus.current) {
      return
    }

    // Don't check if we're already navigating away
    if (location.pathname !== '/dashboard/company') {
      return
    }

    const checkUserStatus = async () => {
      isCheckingStatus.current = true
      try {
        const userData = await authService.getCurrentUser()
        if (userData) {
          const bgvStatus = userData.BGV_status || userData.bgv_status || userData.company?.bgv_status || 'pending'
          const adminReviewStatus = userData.admin_review || userData.admin_review_status || userData.company?.admin_review_status || 'pending'
          const overallStatus = userData.status || 'pending'

          // Normalize status values to lowercase for comparison
          const bgvStatusNormalized = String(bgvStatus).toLowerCase().trim()
          const adminStatusNormalized = String(adminReviewStatus).toLowerCase().trim()
          const overallStatusNormalized = String(overallStatus).toLowerCase().trim()

          // Check if both are complete (same logic as CompanyDocumentUpload)
          const isBGVComplete = bgvStatusNormalized === 'approved' || bgvStatusNormalized === 'completed' || bgvStatusNormalized === 'active'
          const isAdminComplete = adminStatusNormalized === 'approved' || adminStatusNormalized === 'completed' || adminStatusNormalized === 'active'
          const isOverallComplete = overallStatusNormalized === 'approved' || overallStatusNormalized === 'completed' || overallStatusNormalized === 'active'

          // Both approved/completed - stay on dashboard (normal behavior)
          if ((isBGVComplete && isAdminComplete) || isOverallComplete) {
            hasCheckedStatus.current = true
            return
          }

          // Otherwise, redirect to document upload (use replace to prevent back button issues)
          requestAnimationFrame(() => {
            navigate('/company/document-upload', { replace: true })
          })
        }
      } catch (error) {
        console.warn('Error checking user status on dashboard:', error)
        // On error, redirect to document upload to be safe
        requestAnimationFrame(() => {
          navigate('/company/document-upload', { replace: true })
        })
      } finally {
        isCheckingStatus.current = false
        hasCheckedStatus.current = true
      }
    }

    checkUserStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  const recentApplications = [
    {
      id: 1,
      name: 'Sarah Johnson',
      role: 'Senior React Developer',
      experience: '5 years',
      skills: ['React', 'TypeScript', 'Node.js'],
      rating: 4.8,
      status: 'Under Review',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face',
      appliedDate: '2 days ago'
    },
    {
      id: 2,
      name: 'Michael Chen',
      role: 'Full Stack Developer',
      experience: '4 years',
      skills: ['Python', 'Django', 'PostgreSQL'],
      rating: 4.6,
      status: 'Interview Scheduled',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
      appliedDate: '1 day ago'
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      role: 'UI/UX Designer',
      experience: '3 years',
      skills: ['Figma', 'Adobe XD', 'Sketch'],
      rating: 4.9,
      status: 'Shortlisted',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
      appliedDate: '3 days ago'
    }
  ]

  const jobPostings = [
    {
      id: 1,
      title: 'Senior React Developer',
      department: 'Engineering',
      type: 'Full-time',
      location: 'Remote',
      salary: '$80,000 - $120,000',
      applications: 24,
      status: 'Active',
      postedDate: '5 days ago'
    },
    {
      id: 2,
      title: 'Product Manager',
      department: 'Product',
      type: 'Full-time',
      location: 'San Francisco, CA',
      salary: '$90,000 - $130,000',
      applications: 18,
      status: 'Active',
      postedDate: '3 days ago'
    },
    {
      id: 3,
      title: 'DevOps Engineer',
      department: 'Engineering',
      type: 'Contract',
      location: 'New York, NY',
      salary: '$70 - $90/hr',
      applications: 12,
      status: 'Paused',
      postedDate: '1 week ago'
    }
  ]

  const upcomingInterviews = [
    {
      id: 1,
      candidate: 'Sarah Johnson',
      position: 'Senior React Developer',
      time: 'Today, 2:00 PM',
      type: 'Technical Interview',
      interviewer: 'John Smith'
    },
    {
      id: 2,
      candidate: 'Michael Chen',
      position: 'Full Stack Developer',
      time: 'Tomorrow, 10:00 AM',
      type: 'HR Interview',
      interviewer: 'Jane Doe'
    },
    {
      id: 3,
      candidate: 'Emily Rodriguez',
      position: 'UI/UX Designer',
      time: 'Friday, 3:00 PM',
      type: 'Portfolio Review',
      interviewer: 'Mike Johnson'
    }
  ]



  useEffect(() => {
    fetchCompanyDashboard()
  }, [])
  const fetchCompanyDashboard = async () => {
    try {

      const userData = sessionStorage.getItem('userData')
      if (userData) {
        const user = JSON.parse(userData)
        const userId = user.id || user.user_id

        if (userId) {
          const response = await companyService.getCompanyDashboard(userId)

          if (response.data) {
            const apiData = response.data
            setApiData(apiData)
            console.log('Company dashboard data:', apiData)

          }
        }
      }
    } catch (error) {
      console.error('Error fetching Company dashboard data:', error)
      // Don't show error toast for dashboard data fetch to avoid annoying users
    }
  }


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Company Dashboard</h1>
          <p className="text-slate-600 mt-1">Manage your hiring pipeline and talent acquisition</p>
        </div>
        <div className="flex space-x-3">
          {/* <button className="btn btn-outline">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </button> */}
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate("/dashboard/company/jobs")}
          >
            Post Job
          </button>
        </div>
      </div>

      {/* Company Status & Subscription */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Subscription Tier */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Subscription Plan</h3>
            <div className={`p-2 rounded-lg ${apiData.plan_name}`}>
              {subscriptionFeatures[companyStatus.subscriptionTier]?.icon}
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-slate-900">{companyStatus.subscriptionTier} Plan</span>
              <span className={`text-sm font-medium ${subscriptionFeatures[apiData.plan_name]?.color}`}>
                {apiData.status}
              </span>
            </div>
            <div className="space-y-1">
              {subscriptionFeatures[apiData.plan_name]?.features.map((feature, index) => (
                <div key={index} className="flex items-center text-sm text-slate-600">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  {feature}
                </div>
              ))}
            </div>
            <button className="btn btn-outline btn-sm w-full">
              Upgrade Plan
            </button>
          </div>
        </motion.div>

        {/* Compliance Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Compliance Status</h3>
            <Shield className="w-6 h-6 text-primary-600" />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600">Overall Score</span>
              <span className="text-2xl font-bold text-green-600">{companyStatus.complianceScore}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${companyStatus.complianceScore}%` }}
              ></div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center text-yellow-600">
                <AlertTriangle className="w-4 h-4 mr-1" />
                {companyStatus.expiringDocuments} expiring
              </div>
              <div className="flex items-center text-blue-600">
                <Bell className="w-4 h-4 mr-1" />
                {companyStatus.pendingActions} pending
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="card p-6"
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <button className="btn btn-primary btn-sm w-full justify-start">
              <Plus className="w-4 h-4 mr-2" />
              Post New Job
            </button>
            <button className="btn btn-outline btn-sm w-full justify-start">
              <Search className="w-4 h-4 mr-2" />
              Search Talent
            </button>
            <button className="btn btn-outline btn-sm w-full justify-start">
              <FileText className="w-4 h-4 mr-2" />
              View Documents
            </button>
          </div>
        </motion.div>
      </div>

      {/* Compliance Alerts */}
      {complianceAlerts.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Compliance Alerts</h3>
            <Bell className="w-5 h-5 text-yellow-500" />
          </div>
          <div className="space-y-3">
            {complianceAlerts.map((alert, index) => (
              <div key={index} className={`p-3 rounded-lg border ${alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200'
                }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {alert.type === 'warning' ? (
                      <AlertTriangle className="w-4 h-4 text-yellow-600 mr-2" />
                    ) : (
                      <Bell className="w-4 h-4 text-blue-600 mr-2" />
                    )}
                    <span className="text-sm text-gray-700">{alert.message}</span>
                  </div>
                  <button className="text-sm font-medium text-primary-600 hover:text-primary-500">
                    {alert.action}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="card p-6 hover-lift"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">{stat.title}</p>
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className={`text-sm flex items-center mt-1 ${stat.changeType === 'positive' ? 'text-success-600' : 'text-error-600'
                  }`}>
                  <ArrowUpRight className="w-4 h-4 mr-1" />
                  {stat.change}
                </p>
              </div>
              <div className="p-3 bg-primary-100 rounded-lg text-primary-600">
                {stat.icon}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex space-x-8">
          {['overview', 'applications', 'jobs', 'interviews'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-secondary-700'
                }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Applications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Recent Applications</h3>
            <button className="text-primary-600 hover:text-primary-500 text-sm font-medium">
              View all
            </button>
          </div>

          <div className="space-y-4">
            {recentApplications.map((application) => (
              <div key={application.id} className="flex items-center space-x-4 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                <img
                  src={application.avatar}
                  alt={application.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className="flex-1">
                  <h4 className="font-medium text-slate-900">{application.name}</h4>
                  <p className="text-sm text-slate-600">{application.role} • {application.experience}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className="flex items-center">
                      <Star className="w-3 h-3 text-warning-400 fill-current" />
                      <span className="text-xs text-slate-500 ml-1">{application.rating}</span>
                    </div>
                    <span className={`badge ${application.status === 'Interview Scheduled' ? 'badge-primary' :
                      application.status === 'Shortlisted' ? 'badge-success' : 'badge-warning'
                      }`}>
                      {application.status}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">{application.appliedDate}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {application.skills.slice(0, 2).map((skill) => (
                      <span key={skill} className="badge badge-secondary text-xs">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Job Postings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Job Postings</h3>
            <button className="text-primary-600 hover:text-primary-500 text-sm font-medium">
              View all
            </button>
          </div>

          <div className="space-y-4">
            {jobPostings.map((job) => (
              <div key={job.id} className="p-4 border border-slate-200 rounded-lg hover:border-primary-200 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-slate-900">{job.title}</h4>
                  <span className={`badge ${job.status === 'Active' ? 'badge-success' : 'badge-warning'
                    }`}>
                    {job.status}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mb-2">{job.department} • {job.type} • {job.location}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">{job.salary}</span>
                  <span className="text-primary-600 font-medium">{job.applications} applications</span>
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  Posted {job.postedDate}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Upcoming Interviews */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="card p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Upcoming Interviews</h3>
          <button className="text-primary-600 hover:text-primary-500 text-sm font-medium">
            View calendar
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {upcomingInterviews.map((interview) => (
            <div key={interview.id} className="p-4 border border-secondary-200 rounded-lg hover:border-primary-200 transition-colors">
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <h4 className="font-medium text-secondary-900">{interview.candidate}</h4>
                  <p className="text-sm text-secondary-600">{interview.position}</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-secondary-600">
                  <Clock className="w-4 h-4 mr-2" />
                  {interview.time}
                </div>
                <div className="text-sm text-secondary-600">
                  {interview.type} with {interview.interviewer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="card p-6"
      >
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="btn btn-outline justify-start">
            <Briefcase className="w-5 h-5 mr-3" />
            <div className="text-left">
              <div className="font-medium">Post New Job</div>
              <div className="text-sm text-secondary-600">Create job listing</div>
            </div>
          </button>
          <button className="btn btn-outline justify-start">
            <Search className="w-5 h-5 mr-3" />
            <div className="text-left">
              <div className="font-medium">Search Talent</div>
              <div className="text-sm text-secondary-600">Find candidates</div>
            </div>
          </button>
          <button className="btn btn-outline justify-start">
            <Calendar className="w-5 h-5 mr-3" />
            <div className="text-left">
              <div className="font-medium">Schedule Interview</div>
              <div className="text-sm text-secondary-600">Book meeting</div>
            </div>
          </button>
        </div>
      </motion.div>
    </div>
  )
}

export default CompanyDashboard
